import { Amplify } from "aws-amplify";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { BrowserRouter } from "react-router-dom";
import "./App.css";
import AppErrorBoundary from "./components/AppErrorBoundary/AppErrorBoundary";
import Header from "./components/Header/Header";
import awsExports from "./lib/aws-exports";
import { AUTH_DOMAIN, WEB_URL } from "./lib/constants";
import { ChatSessionsContextProvider } from "./lib/contexts/ChatSessionsProvider";
import { CustomThemeContextProvider } from "./lib/contexts/ThemeProvider";
import { UserInfoContextProvider } from "./lib/contexts/UserInfoProvider";
import { Auth } from "./pages/auth/auth";
import { isMainSiteAuthorized } from "@common/domain/auth";
import Call from "./pages/call/call";
import Unauthorized from "./pages/unauthorized/unauthorized";
import AdminPage from "./pages/admin/AdminPage";
import SettingsPage from "./pages/settings/SettingsPage";
import TextChatPage from "./pages/text-chat/TextChatPage";
import EvalSimPage from "./pages/eval-chat/EvalSimPage";
import AudioNotesPage from "./pages/audio-notes/AudioNotesPage";
import { AdminAuth } from "./components/Auth/AdminAuth";
import { SuperAdminAuth } from "./components/Auth/SuperAdminAuth";

export interface IAccessCreds {
  accessToken: string;
  refreshToken: string;
}

const NavigateToTextChat = () => {
  const { urlChatSession } = useParams();
  return <Navigate to={`/text-chat/${urlChatSession}`} replace />;
};

Amplify.configure({
  Auth: {
    region: awsExports.REGION,
    userPoolId: awsExports.USER_POOL_ID,
    userPoolWebClientId: awsExports.USER_POOL_APP_CLIENT_ID,
    mandatorySignIn: true,
    authenticationFlowType: "USER_PASSWORD_AUTH",
    oauth: {
      domain: AUTH_DOMAIN,
      scope: ["email", "openid"],
      redirectSignIn: WEB_URL,
      redirectSignOut: WEB_URL,
      responseType: "code",
    },
  },
});

export const Root = () => {
  return (
    <div>
      <Routes>
        <Route element={<CustomThemeContextProvider />}>
          <Route element={<UserInfoContextProvider />}>
            <Route element={<ChatSessionsContextProvider />}>
              <Route element={<Header />}>
                <Route element={<Auth isAuthorized={isMainSiteAuthorized} />}>
                  <Route path="/" element={<Call />} />
                  <Route path="/chat" element={<Navigate to="/text-chat" replace />} />
                  <Route path="/chat/:urlChatSession" element={<NavigateToTextChat />} />
                  <Route path="/learn" element={<Navigate to="/" replace />} />
                  <Route path="/learn/*" element={<Navigate to="/" replace />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/text-chat" element={<TextChatPage />} />
                  <Route path="/text-chat/:urlChatSession" element={<TextChatPage />} />
                  <Route element={<AdminAuth />}>
                    <Route path="/admin/audio-notes" element={<AudioNotesPage />} />
                    <Route path="/admin/eval-sim" element={<EvalSimPage />} />
                    <Route path="/admin" element={<AdminPage />} />
                    <Route path="/admin/graph" element={<AdminPage />} />
                    <Route path="/admin/prompts" element={<AdminPage />} />
                    <Route path="/admin/feedback" element={<AdminPage />} />
                    <Route path="/admin/goals" element={<AdminPage />} />
                    <Route path="/admin/methodology-presets" element={<AdminPage />} />
                    <Route path="/admin/ingestions" element={<AdminPage />} />
                    <Route path="/admin/transcripts" element={<AdminPage />} />
                    <Route path="/admin/conversation-results" element={<AdminPage />} />
                    <Route path="/admin/synapse" element={<AdminPage />} />
                    <Route path="/admin/synapse/demo" element={<AdminPage />} />
                  </Route>
                  <Route element={<SuperAdminAuth />}>
                    <Route path="/admin/errors" element={<AdminPage />} />
                  </Route>
                </Route>
                <Route path="/unauthorized" element={<Unauthorized />} />
              </Route>
            </Route>
          </Route>
        </Route>
      </Routes>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AppErrorBoundary>
        <Root />
      </AppErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
